import React, { useState } from 'react';
import { Offcanvas, Nav, Card } from 'react-bootstrap';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';

import useTableData from 'hooks/useTableData';
import useAdvanceTable from 'hooks/useAdvanceTable';

import TableBody from 'components/common/Table/TableBody';
import TablePagination from 'components/common/Table/TablePagination';

import { picklistApi } from 'api';

import { PICKLIST_PROCESS_SHIPMENT_COLUMNS } from 'constants/picklist';

const COMPLETED_STATUSES = [
  'packed',
  'ready_to_ship',
  'added_to_manifest',
  'dispatched',
  'delivered',
  'splitted',
  'return_acknowledged',
  'returned',
  'canceled',
];

const TAB_OPTIONS = [
  { key: 'picking', label: 'Not Started' },
  { key: 'picked', label: 'Partially Packed' },
  { key: 'sidelined', label: 'Sidelined' },
  { key: 'completed', label: 'Completed' },
];

const PicklistShipmentsTable = ({ picklistId }) => {
  const [activeTab, setActiveTab] = useState('picking');

  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 25;

  const { data, totalItems, loading, error } = useTableData({
    apiService: picklistApi,
    method: 'getShipmentsOfPicklist',
    queryParams: {
      pageIndex,
      pageSize,
      filters: { status: activeTab },
    },
    resourceId: picklistId,
    customConfig: {
      transformParams: params => {
        const transformedParams = new URLSearchParams();

        transformedParams.append('page', params.pageIndex + 1);
        transformedParams.append('page_size', params.pageSize);

        if (params.filters?.status === 'completed') {
          COMPLETED_STATUSES.forEach(status => {
            transformedParams.append('shipment_status', status);
          });
        } else {
          transformedParams.append('shipment_status', params.filters?.status);
        }

        return transformedParams;
      },
    },
  });

  const handleTabChange = newTab => {
    setPageIndex(0);
    setActiveTab(newTab);
  };

  const table = useAdvanceTable({
    data: data || [],
    columns: PICKLIST_PROCESS_SHIPMENT_COLUMNS,
    manualPagination: true,
    sortable: false,
    totalItems,
    paginationState: { pageIndex, pageSize },
    onPaginationChange: ({
      pageIndex: newPageIndex,
      pageSize: newPageSize,
    }) => {
      setPageIndex(newPageIndex);
    },
  });

  return (
    <div>
      <div className="overflow-x-auto">
        <Nav variant="tabs" className="mb-3 flex flex-row flex-nowrap">
          {TAB_OPTIONS.map(tab => (
            <Nav.Item key={tab.key} className="flex-shrink-0">
              <Nav.Link
                active={activeTab === tab.key}
                onClick={() => handleTabChange(tab.key)}
                className="cursor-pointer outline-none"
              >
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>

      <AdvanceTableProvider {...table}>
        <Card className="mb-3">
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center p-4">Loading...</div>
            ) : error ? (
              <div className="text-center p-4 text-danger">{error}</div>
            ) : data?.length === 0 ? (
              <div className="text-center p-4">No shipments found</div>
            ) : (
              <TableBody
                headerClassName="bg-200 text-nowrap align-middle text-center"
                rowClassName="align-middle white-space-nowrap text-center"
                tableProps={{
                  size: 'sm',
                  striped: true,
                  className: 'fs-10 mb-0 overflow-hidden',
                }}
              />
            )}
          </Card.Body>
          <Card.Footer>
            <TablePagination />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
    </div>
  );
};

const PicklistShipmentsPanel = ({ show, onHide, picklistId }) => {
  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="w-75">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>View Shipments</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0">
        <PicklistShipmentsTable picklistId={picklistId} />
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default PicklistShipmentsPanel;
